import { useUserStore } from "@mirage/store/store";
import BigLogo from "@publicPortal/components/bigLogo/BigLogo";
import ResultBoard from "@publicPortal/components/resultBoard/ResultBoard";
import { ConsulteeService } from "@publicPortal/services/ConsulteeService";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { memo, useCallback, useEffect, useState } from "react";
import { usePlanningApplicationDetailsStore } from "@publicPortal/store/store";
import { RecordDetailsTabNames } from "@publicPortal/utils/enums";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { wait } from "@shared/mirage/helpers/Wait";
import { useInitializeUserSession } from "@mirage/hooks/useInitializeUserSession";
import { PPConstants } from "@shared/publicportal/constants";

const consulteeService = new ConsulteeService();
const pp = new PublicPortalRoutingService();

const VerifyConsultee = memo(() => {
    const initialState = {
        message: "",
        error: "",
    } as IVerifyConsulteeMessage;
    const consulteeTokenString = localStorage.getItem("verifyConsultee.token");
    const urlParams = new URL(window.location.href).searchParams;
    const token = urlParams.get("t");
    const setUser = useUserStore((state) => state.setUser);
    const navigate = useStableNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [consulteeVerification, setConsulteeVerification] = useState<IVerifyConsulteeMessage>(initialState);
    const updatePlanningApplicationTab = usePlanningApplicationDetailsStore((s) => s.updatePlanningApplicationTab);
    const setInitialUser = useInitializeUserSession(pp.Root);
    const updateConsulteeVerification = useCallback((message: string, error?: string) => {
        setConsulteeVerification(() => ({ message, error }));
    }, []);
    const redirectUserToPlanningApplication = useCallback(
        (paId: number) => {
            if (!paId) {
                updateConsulteeVerification("Could not redirect to planning record", "No paId found");
                return;
            }
            updatePlanningApplicationTab(paId, RecordDetailsTabNames.Consultations);
            navigate(pp.PlanningApplicationRecordRoute(paId), { replace: true });
        },
        [navigate, updatePlanningApplicationTab, updateConsulteeVerification]
    );

    useEffect(() => {
        async function verifyConsultee() {
            if (!token) {
                updateConsulteeVerification("Token is required to verify consultee", "No token found");
                setIsLoading(false);
                return;
            }
            const verificationResult = await consulteeService.verifyConsultee({ token });
            const consulteeTokenDataToStore = verificationResult?.consulteeDetails;
            const loginResult = verificationResult.loginResult;
            if (!consulteeTokenDataToStore || !loginResult) {
                updateConsulteeVerification("Something went wrong", "Could not login");
                setIsLoading(false);
                return;
            }
            if (verificationResult.error) {
                updateConsulteeVerification(verificationResult.error || "Something went wrong", verificationResult.error || "error");
                setIsLoading(false);
                return;
            }
            if (consulteeTokenString) {
                localStorage.removeItem("verifyConsultee.token");
            }
            setUser({ ...loginResult, first_name: loginResult.userName, last_name: loginResult.userLastName, id: Number(loginResult.userId) });
            setInitialUser(PPConstants.AppName);
            localStorage.setItem("verifyConsultee.token", JSON.stringify(consulteeTokenDataToStore));
            setIsLoading(false);
            updateConsulteeVerification("You will be taken to a new page shortly.");
            await wait(1000);
            redirectUserToPlanningApplication(consulteeTokenDataToStore.paId);
        }
        verifyConsultee();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <BigLogo />
            <ResultBoard
                headerText="Verify Consultee"
                loading={isLoading}
                success={!consulteeVerification.error}
                message={consulteeVerification.message}
                buttonLabel="Go home"
                buttonNavTo={pp.Root}
            />
        </>
    );
});

export interface IVerifyConsulteeMessage {
    message: string;
    error?: string;
}

export default VerifyConsultee;
